import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban, Plus, Search, Building2, Users,
  Calendar, MapPin, Shield, Globe, RefreshCw
} from 'lucide-react';
import api from '../../services/apiService';

const OEMProjectsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [canCreateProjects, setCanCreateProjects] = useState(true);

  useEffect(() => {
    fetchProjects();
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    try {
      const response = await api.get('/oem/dashboard/team/');
      const roles = response.data.my_roles || [];
      const hasMasterRole = roles.some(r => r.role === 'master');
      const hasOnlyRestrictedRoles = roles.length > 0 && !hasMasterRole;
      if (hasOnlyRestrictedRoles) {
        setCanCreateProjects(false);
      }
    } catch (err) {
      // If endpoint fails, user has no team roles - allow access
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/oem/projects/');
      setProjects(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusStyle = (status) => {
    const styles = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
      active: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      completed: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      archived: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
    };
    return styles[status] || styles.draft;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    completed: projects.filter(p => p.status === 'completed').length,
    draft: projects.filter(p => p.status === 'draft').length,
  };

  const statusFilters = [
    { key: 'all', label: 'ALL', count: stats.total },
    { key: 'active', label: 'ACTIVE', count: stats.active },
    { key: 'completed', label: 'COMPLETED', count: stats.completed },
    { key: 'draft', label: 'DRAFT', count: stats.draft },
  ];

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm">Loading projects...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Globe className="h-4 w-4 text-orange-500" />
                </div>
                <div>
                  <h1 className="text-base font-black text-slate-900 tracking-tight">OEM PROJECTS</h1>
                  <p className="text-xs text-slate-500">Manage all your OEM projects</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchProjects}
                  className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4 text-slate-600" />
                </button>
                {canCreateProjects && (
                  <button
                    onClick={() => navigate('/oem/projects/create')}
                    className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px]"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create Project
                  </button>
                )}
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-3 mt-4">
              {/* Status Filter Tabs */}
              <div className="flex items-center gap-1.5">
                {statusFilters.map(filter => (
                  <button
                    key={filter.key}
                    onClick={() => setStatusFilter(filter.key)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${
                      statusFilter === filter.key
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {filter.label}
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] ${
                      statusFilter === filter.key ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 pl-9 pr-3 w-64 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-xs"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-3 mb-5">
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-black text-slate-900">{stats.total}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Total</p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                    <FolderKanban className="h-4 w-4 text-slate-600" />
                  </div>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-black text-emerald-600">{stats.active}</p>
                    <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wide">Active</p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Shield className="h-4 w-4 text-emerald-600" />
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-black text-blue-600">{stats.completed}</p>
                    <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wide">Completed</p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-black text-slate-600">{stats.draft}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Draft</p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-slate-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Projects Grid */}
            {filteredProjects.length === 0 ? (
              <div className="bg-slate-50 rounded-xl p-10 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FolderKanban className="h-7 w-7 text-slate-400" />
                </div>
                <h3 className="text-sm font-black text-slate-900 mb-1">No projects found</h3>
                <p className="text-xs text-slate-500 mb-4">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Get started by creating your first OEM project'}
                </p>
                {!searchTerm && statusFilter === 'all' && canCreateProjects && (
                  <button
                    onClick={() => navigate('/oem/projects/create')}
                    className="h-8 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] mx-auto"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create Project
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredProjects.map(project => {
                  const style = getStatusStyle(project.status);
                  return (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/oem/projects/${project.id}`)}
                      className="bg-slate-50 rounded-xl p-4 cursor-pointer hover:bg-slate-100 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                            <Globe className="h-4 w-4 text-orange-500" />
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-orange-500 uppercase tracking-wide">{project.code}</span>
                            <h3 className="text-sm font-bold text-slate-900">{project.name}</h3>
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold rounded-md uppercase ${style.bg} ${style.text}`}>
                          <span className={`w-1 h-1 rounded-full ${style.dot}`} />
                          {project.status}
                        </span>
                      </div>

                      {project.description && (
                        <p className="text-xs text-slate-500 mb-3 line-clamp-2">{project.description}</p>
                      )}

                      <div className="space-y-1.5 text-xs mb-3">
                        {project.location && (
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <MapPin className="h-3 w-3 text-slate-400" />
                            <span>{project.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Calendar className="h-3 w-3 text-slate-400" />
                          <span>{formatDate(project.start_date)} - {formatDate(project.end_date)}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center">
                            <Building2 className="h-3 w-3 text-blue-600" />
                          </div>
                          <span className="text-xs font-bold text-slate-900">{project.companies_count || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-6 rounded-md bg-violet-100 flex items-center justify-center">
                            <Users className="h-3 w-3 text-violet-600" />
                          </div>
                          <span className="text-xs font-bold text-slate-900">{project.technicians_count || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center">
                            <Shield className="h-3 w-3 text-emerald-600" />
                          </div>
                          <span className="text-xs font-bold text-emerald-600">{project.authorized_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default OEMProjectsList;
